import { defaultConfig } from "@tamagui/config/v4";

import { createFont, createTamagui, createTokens, isWeb } from "tamagui";

const tokens = createTokens({
  color: {
    primary: "#ffd84e",
    mutedBackground: "#e9e9e9",
    secondary: "#f2f2f2",
    hot: "#ef5f0d",
    success: "rgb(48, 164, 108)",
    info: "rgb(0, 145, 255)",
    danger: "rgb(220, 61, 67)",

    // customer overrides
    pressablePrimaryLabel: "#000000",
    pressablePrimaryBackground: "#ffd84e",
  },
  space: {
    small: 6,
    medium: 12,
    true: 12,
    large: 18,
    larger: 24,
    largest: 30,
  },
  size: {
    smallest: 11,
    smaller: 14,
    small: 16,
    medium: 32,
    true: 24,
    normal: 24,
    large: 48,
  },
  zIndex: {
    $small: 10,
    $medium: 100,
    $large: 100000,
    $huge: 1000000,
    $true: 10,
  },
  radius: {
    true: 12,
  },
});

const fontFamily = isWeb
  ? 'Inter, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
  : "Inter";

const bodyFont = createFont({
  family: fontFamily,
  size: { true: 16 },
  lineHeight: { true: 20 },
  weight: { true: "300" },
  letterSpacing: {},
  face: {
    300: { normal: "InterMedium", italic: "InterMedium-Italic" },
  },
});

const labelFont = createFont({
  family: fontFamily,
  size: { true: 16 },
  lineHeight: { true: 20 },
  weight: { true: "700" },
  letterSpacing: {},
  face: {
    700: { normal: "InterBold", italic: "InterBold-Italic" },
  },
});

const titleFont = createFont({
  family: fontFamily,
  size: { true: 24 },
  lineHeight: { true: 33 },
  weight: { true: "700" },
  letterSpacing: {},
  face: {
    700: { normal: "InterBold", italic: "InterBold-Italic" },
  },
});

const subtextFont = createFont({
  family: fontFamily,
  size: { true: 14 },
  lineHeight: { true: 16 },
  weight: { true: "300" },
  letterSpacing: {},
  face: {
    300: { normal: "InterMedium", italic: "InterMedium-Italic" },
  },
});

const subtextBoldFont = createFont({
  family: fontFamily,
  size: { true: 14 },
  lineHeight: { true: 16 },
  weight: { true: "700" },
  letterSpacing: {},
  face: {
    700: { normal: "InterBold", italic: "InterBold-Italic" },
  },
});

const tamaguiConfig = createTamagui({
  ...defaultConfig,
  settings: {
    ...defaultConfig.settings,
    onlyAllowShorthands: false,
  },
  fonts: {
    body: bodyFont,
    label: labelFont,
    heading: titleFont,
    subtext: subtextFont,
    subtextLabel: subtextBoldFont,
  },
  tokens,
  defaultFont: "body",
  media: {
    xs: { maxWidth: 430 },
    gtXs: { minWidth: 430 + 1 },
    sm: { maxWidth: 600 },
    gtSm: { minWidth: 600 + 1 },
    md: { maxWidth: 980 },
    gtMd: { minWidth: 980 + 1 },
    lg: { maxWidth: 1120 },
    gtLg: { minWidth: 1120 + 1 },
    short: { maxHeight: 820 },
    tall: { minHeight: 820 },
    hoverNone: { hover: "none" },
    pointerCoarse: { pointer: "coarse" },
  },
  themes: {
    dark: {
      background: "#0d0d0d",
      backgroundBlock: "#000",
      backgroundMutedBlock: "#000",
      color: "#fff",
      muted: "#858585",
      inverted: "#000",
      divider: "#232323",
      borderColor: "#232323",
      primary: tokens.color.primary,
      secondary: tokens.color.secondary,
      hot: tokens.color.hot,

      success: "rgb(60, 177, 121)",
      info: "rgb(54, 158, 255)",
      danger: "rgb(242, 85, 90)",

      pressablePrimaryLabel: tokens.color.pressablePrimaryLabel,
      pressablePrimaryBackground: tokens.color.pressablePrimaryBackground,
    },
    light: {
      background: "#f2f2f2",
      backgroundBlock: "#fff",
      backgroundMutedBlock: tokens.color.mutedBackground,
      color: "#000",
      muted: "#7a7a7a",
      inverted: "#fff",
      divider: "#dcdcdc",
      borderColor: "#dcdcdc",
      primary: tokens.color.primary,
      secondary: tokens.color.secondary,
      hot: tokens.color.hot,
      success: tokens.color.success,
      info: tokens.color.info,
      danger: tokens.color.danger,

      pressablePrimaryLabel: tokens.color.pressablePrimaryLabel,
      pressablePrimaryBackground: tokens.color.pressablePrimaryBackground,
    },
  },
});

export default tamaguiConfig;

export type Conf = typeof tamaguiConfig;

// make imports typed
declare module "@tamagui/core" {
  interface TamaguiCustomConfig extends Conf {}
}
